/**
 * DeviceCapabilityDiagnostic — staff-only 3-column capability matrix view.
 * Spec §6.3 / Phase 7 T073.
 *
 * Renders a side-by-side diff of:
 *   1. `matrixCapabilities`     — plan ∩ classification ∩ deviceStateGate
 *                                 (what the matrix allows; staff-only field)
 *   2. `reportedCapabilities`   — device-reported; narrows (1) when present
 *   3. `availableCommands`      — final resolved set delivered to the UI
 *
 * For each catalog command we render one row with a ✓/✗ badge per column and
 * an annotation explaining WHY it was blocked:
 *   - `blocked:classification` — matrix didn't allow it (plan / predicate / gate)
 *   - `blocked:plan`           — plan feature flag disabled  (folded under
 *                                 classification from the client's POV —
 *                                 server-side reason may refine the label
 *                                 further when `buildReason` data lands)
 *   - `blocked:reported`       — reported capabilities narrowed it out
 *   - `blocked:fleet-policy`   — fleet deny-list stripped it
 *
 * Set-math (all three arrays are subsets of the catalog):
 *   matrix \ reported              → narrowed away by device reporting
 *   matrix \ resolved              → either reported OR fleet stripped it
 *   matrix ∩ reported \ resolved   → stripped by fleet (deny-list)
 *
 * Edge case (documented): a command appearing in `reportedCapabilities` but
 * NOT in `matrixCapabilities` cannot happen in the normal data path
 * (`reportedCapabilities` can only narrow the matrix per spec §9.1). We still
 * render it with a "reported-only" annotation so staff can spot a broken
 * player reporting a command the matrix would never grant — that's a bug
 * signal, not a normal state.
 */
import { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@staff-app/ui';
import { CATALOG_BY_ID, MDM_COMMANDS_CATALOG, type MdmCommandId } from '@staff-app/shared-types';
import type { MdmDevice } from '@/features/mdm-devices/hooks/useStaffCustomerDevices';

interface Props {
  device: MdmDevice;
}

type BlockReason =
  | 'none'
  | 'blocked:classification'
  | 'blocked:reported'
  | 'blocked:fleet_policy'
  | 'reported-only'; // unexpected; documented above

interface RowState {
  id: MdmCommandId;
  i18nKey: string;
  inMatrix: boolean;
  inReported: boolean | null; // null when device did not report capabilities at all
  inResolved: boolean;
  reason: BlockReason;
}

function isKnownId(id: string): id is MdmCommandId {
  return id in CATALOG_BY_ID;
}

function toSet<T extends string>(list: readonly string[] | null | undefined): Set<T> | null {
  if (list === null || list === undefined) return null;
  return new Set(list as T[]);
}

export function DeviceCapabilityDiagnostic({ device }: Props) {
  const rows = useMemo<RowState[]>(() => {
    const matrix = toSet<MdmCommandId>(device.matrixCapabilities) ?? new Set<MdmCommandId>();
    const reportedRaw = toSet<string>(device.reportedCapabilities);
    const resolved = toSet<MdmCommandId>(device.availableCommands) ?? new Set<MdmCommandId>();

    // Catalog provides row order — stable, exhaustive, client-known ids only.
    // Additionally surface any id present in one of the sets but missing from
    // the catalog (forward-compat / data-quality signal) so staff notice.
    const extraIds = new Set<string>();
    const collect = (s: Set<string> | null) => {
      if (!s) return;
      s.forEach((id) => {
        if (!isKnownId(id)) extraIds.add(id);
      });
    };
    collect(matrix);
    collect(reportedRaw);
    collect(resolved);

    const catalogRows: RowState[] = MDM_COMMANDS_CATALOG.map((cmd) => {
      const inMatrix = matrix.has(cmd.id);
      const inReported = reportedRaw === null ? null : reportedRaw.has(cmd.id);
      const inResolved = resolved.has(cmd.id);

      // Resolution order of the blocked-reason annotation:
      //   1. Resolved → no block
      //   2. In matrix but not resolved:
      //        2a. Reported exists and doesn't include this id → blocked:reported
      //        2b. Otherwise → blocked:fleet_policy
      //   3. Not in matrix → blocked:classification (plan / predicate / gate)
      let reason: BlockReason = 'none';
      if (!inResolved) {
        if (inMatrix) {
          if (inReported === false) reason = 'blocked:reported';
          else reason = 'blocked:fleet_policy';
        } else {
          reason = 'blocked:classification';
        }
      }

      return {
        id: cmd.id,
        i18nKey: cmd.i18nKey,
        inMatrix,
        inReported,
        inResolved,
        reason,
      };
    });

    // Trailing "unknown ids" rows (data-quality alerts)
    const extraRows: RowState[] = [...extraIds].map((id) => {
      const inMatrix = matrix.has(id as MdmCommandId);
      const inReported = reportedRaw === null ? null : reportedRaw.has(id);
      const inResolved = resolved.has(id as MdmCommandId);
      return {
        id: id as MdmCommandId,
        i18nKey: id, // unknown id — fall back to the raw string
        inMatrix,
        inReported,
        inResolved,
        reason: inReported && !inMatrix ? 'reported-only' : 'blocked:classification',
      };
    });

    return [...catalogRows, ...extraRows];
  }, [device.matrixCapabilities, device.reportedCapabilities, device.availableCommands]);

  return (
    <div
      className="rounded-lg border bg-card p-3 shadow-sm"
      data-testid="device-capability-diagnostic"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-muted-foreground">{'Capabilities'}</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[32%]">{'Command'}</TableHead>
            <TableHead className="w-[16%]">{'Matrix'}</TableHead>
            <TableHead className="w-[16%]">{'Reported'}</TableHead>
            <TableHead className="w-[16%]">{'Resolved'}</TableHead>
            <TableHead className="w-[20%]">{'Reason'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} data-testid={`capability-row-${row.id}`}>
              <TableCell className="font-mono text-xs">{row.i18nKey}</TableCell>
              <TableCell>
                <BoolCell value={row.inMatrix} />
              </TableCell>
              <TableCell>
                <BoolCell value={row.inReported} />
              </TableCell>
              <TableCell>
                <BoolCell value={row.inResolved} />
              </TableCell>
              <TableCell>
                <ReasonBadge reason={row.reason} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell helpers
// ---------------------------------------------------------------------------

function BoolCell({ value }: { value: boolean | null }) {
  if (value === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return value ? (
    <Check className="h-4 w-4 text-emerald-600" aria-label="allowed" />
  ) : (
    <X className="h-4 w-4 text-muted-foreground" aria-label="blocked" />
  );
}

function ReasonBadge({ reason }: { reason: BlockReason }) {
  if (reason === 'none') return null;

  const label =
    reason === 'blocked:classification'
      ? 'blocked:classification'
      : reason === 'blocked:reported'
        ? 'blocked:reported'
        : reason === 'blocked:fleet_policy'
          ? 'blocked:fleet-policy'
          : 'reported-only';

  const tone =
    reason === 'blocked:reported' || reason === 'blocked:fleet_policy'
      ? 'default'
      : reason === 'reported-only'
        ? 'destructive' // unexpected; flag it
        : 'secondary';

  return (
    <Badge variant={tone} className={cn('font-mono text-[10px]')}>
      {label}
    </Badge>
  );
}
